import React, {FC, useRef} from 'react';
import {ScrollView, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';

import Text from '/component/base/Text';
import TextInput from '/component/base/TextInput';
import ModalHeader from '/component/layout/ModalHeader';
import {DownlineProfile} from '/constant/TestIDs';
import {withPopup} from '/provider/PopupProvider';
import {useTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {withHOC} from '/util/hoc.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './ProfileEditNotesPage.style';
import {handleBackButton, handleEditNotes} from './ProfileEditNotesPage.util';

interface ProfileEditNotesPageProps {
  notes: string;
  notesUpdatedTs: string;
  updateNotes: (id: string, body: {notes: string}) => void;
  recordId: string;
  showPopup: () => void;
  hidePopup: () => void;
  setState: (state: any) => void;
  userType: string;
  lastPaidRank: number | null;
  coachHighestRank?: string;
  coachCurrentRank?: string;
  coachId?: string;
}

export const ProfileEditNotesPage: FC<ProfileEditNotesPageProps> = ({
  notes = '',
  notesUpdatedTs = '',
  updateNotes = () => {},
  recordId: id = '',
  showPopup = withPopup.showPopupDefaultProp,
  hidePopup = withPopup.hidePopupDefaultProp,
  setState = () => {},
  userType = '',
  lastPaidRank = null,
  coachHighestRank = 'na',
  coachCurrentRank = 'na',
  coachId = 'na',
}) => {
  const navigation = useNavigation();
  const {tt} = useTranslation('EditNotes');
  const dateUtil = getDateUtilDefaultInstance();
  const formattedDate = dateUtil.formatDate(notesUpdatedTs, 'MMMM d, yyyy');

  const [value, onChangeText] = React.useState(notes);
  const editNotesBody = {
    notes: value,
  };
  const scrollViewRef = useRef();
  const handleNotesCount = value ? `${value.length}/1000` : '0/1000';

  const scrollViewContentStyle = {maxHeight: '100%'};

  return (
    <ModalHeader
      isProfileScreen
      onClose={() =>
        handleBackButton({
          notes,
          id,
          body: editNotesBody,
          navigation,
          showPopup,
          hidePopup,
          userType,
          lastPaidRank,
          coachHighestRank,
          coachCurrentRank,
          coachId,
        })
      }
      closeIconName={'chevron-left'}
      title={tt('EditNotes', 'Edit notes')}
      actionLabel={tt('save', 'Save')}
      onActionPress={() =>
        handleEditNotes({
          updateNotes,
          id,
          navigation,
          body: editNotesBody,
          setState,
          notes,
          userType,
          lastPaidRank,
          coachHighestRank,
          coachCurrentRank,
          coachId,
        })
      }
      style={{base: styles.base}}>
      <View style={styles.notesContainer}>
        {notes && (
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            style={styles.dateText}
            {...testProperties(DownlineProfile.candidateNotesEditLastChange)}>
            {tt(
              'last.notes.change.relative',
              generateComponentCallback({
                component: <>Last change made {formattedDate}</>,
              }),
            )}
          </Text>
        )}
        <View style={styles.textInputContainer}>
          <ScrollView
            // @ts-expect-error TS(2769): No overload matches this call.
            ref={scrollViewRef}
            onContentSizeChange={() =>
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              scrollViewRef.current.scrollToEnd({animated: true})
            }
            contentContainerStyle={scrollViewContentStyle}>
            <TextInput
              value={value}
              multiline
              maxLength={1000}
              autoFocus
              scrollEnabled
              placeholder={'Write something down...'}
              editable
              onChangeText={(text: any) => onChangeText(text)}
              {...testProperties(DownlineProfile.candidateNotesEditInput)}
            />
          </ScrollView>
        </View>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          style={styles.notesCount}
          variant="subheading"
          {...testProperties(DownlineProfile.candidateNotesEditCharsCount)}>
          {handleNotesCount}
        </Text>
      </View>
    </ModalHeader>
  );
};

ProfileEditNotesPage.defaultProps = {
  coachHighestRank: 'na',
  coachCurrentRank: 'na',
  coachId: 'na',
};

export default withHOC(withPopup())(ProfileEditNotesPage);
